import { useCallback, useEffect, useRef, useState } from 'react';

export interface FlyoutAnchor {
    left: number;
    top: number;
}

export interface AnchoredFlyout {
    key: string;
    /** Null when the caller anchors the panel itself (e.g. GroupNav's rail). */
    anchor: FlyoutAnchor | null;
}

export interface UseAnchoredFlyoutOptions {
    /** The mode the flyout exists in (collapsed sidebar / folded rail); turning it off closes it. */
    enabled: boolean;
    /** data-* selector marking the portalled panel so clicks inside it don't dismiss. */
    markerSelector?: string;
    /** Close on window resize when the stored anchor would go stale. */
    closeOnResize?: boolean;
}

/**
 * Open/dismiss state for a portalled flyout anchored to a collapsed nav rail:
 * toggles per key, closes on Escape, outside click, disable, and (optionally) resize.
 */
export function useAnchoredFlyout<TContainer extends HTMLElement = HTMLElement>({
    enabled,
    markerSelector = '[data-sidebar-flyout]',
    closeOnResize = false
}: UseAnchoredFlyoutOptions) {
    const [flyout, setFlyout] = useState<AnchoredFlyout | null>(null);
    // The host region (rail/sidebar) - clicks inside it don't count as outside.
    const containerRef = useRef<TContainer | null>(null);

    const close = useCallback(() => setFlyout(null), []);
    // Same key toggles closed; a different key re-anchors in place.
    const toggle = useCallback((key: string, anchor?: FlyoutAnchor) => {
        setFlyout((prev) => (prev?.key === key ? null : { key, anchor: anchor ?? null }));
    }, []);

    useEffect(() => {
        if (!enabled) setFlyout(null);
    }, [enabled]);

    // Boolean dep so listeners don't rebind when only the anchor changes.
    const isOpen = flyout !== null;
    useEffect(() => {
        if (!enabled || !isOpen) return;
        const handleKey = (e: KeyboardEvent) => {
            if (e.key === 'Escape') setFlyout(null);
        };
        const handleClick = (e: MouseEvent) => {
            const target = e.target as HTMLElement | null;
            if (!target) return;
            if (containerRef.current?.contains(target)) return;
            if (target.closest(markerSelector)) return;
            setFlyout(null);
        };
        const handleResize = () => setFlyout(null);
        document.addEventListener('keydown', handleKey);
        document.addEventListener('mousedown', handleClick);
        if (closeOnResize) window.addEventListener('resize', handleResize);
        return () => {
            document.removeEventListener('keydown', handleKey);
            document.removeEventListener('mousedown', handleClick);
            if (closeOnResize) window.removeEventListener('resize', handleResize);
        };
    }, [enabled, isOpen, markerSelector, closeOnResize]);

    return { flyout, toggle, close, containerRef };
}
